import React from "react";
import { DEFAULT_FILTERS } from "../constants";

const FiltersPanel = ({
  showFilters,
  filters,
  toggleFilter,
  toggleMostlyEmptyColumnsFilter,
  updateMostlyEmptyColumnsThreshold,
  isDarkMode,
  sortedData,
  data,
  processedData,
  columns,
  resetFilters,
  handleToggleFilters,
}) => {
  if (!showFilters) return null;

  return (
    <div className={`fixed inset-0 z-20 flex items-center justify-center p-4 transition-opacity duration-300 ${showFilters ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
      {/* Modal backdrop */}
      <div className="absolute inset-0 bg-black/50 transition-opacity duration-300" onClick={handleToggleFilters} />

      {/* Modal content */}
      <div
        className={`relative w-full max-w-md max-h-[80vh] overflow-y-auto rounded-xl shadow-2xl transition-all duration-300 transform ${
          showFilters ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
        } ${isDarkMode ? "bg-black border border-gray-800" : "bg-white border border-gray-300"}`}
      >
        {/* Header with close button */}
        <div className={`p-4 sticky top-0 z-10 border-b ${isDarkMode ? "border-gray-800 bg-black" : "border-gray-200 bg-white"} rounded-t-xl`}>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-bold flex items-center">
              <span className="mr-1.5">🔍</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FFE100] to-[#FFAA00]">Filters</span>
            </h3>
            <div className="flex items-center gap-2">
              {(Object.values(filters).some(Boolean) || filters.hideMostlyEmptyColumns?.enabled) && (
                <button onClick={resetFilters} className="text-2xs font-medium px-2 py-0.5 rounded-full transition-colors duration-200 text-black bg-[#FFE100] hover:bg-[#FFAA00]">
                  Reset All
                </button>
              )}
              {/* Close button */}
              <button
                onClick={handleToggleFilters}
                className={`p-1 rounded-full transition-colors duration-200 ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-800" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"}`}
                aria-label="Close filters"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className={`text-2xs mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            {sortedData.processedData.length}/{data.length} rows · {processedData.displayColumns.length}/{columns.length} columns
          </div>
        </div>

        {/* Filter options */}
        <div className="p-4 flex flex-col gap-3">
          {Object.keys(DEFAULT_FILTERS)
            .filter((key) => key !== "hideMostlyEmptyColumns")
            .map((filterName) => (
              <button
                key={filterName}
                onClick={() => toggleFilter(filterName)}
                className={`px-3 py-2 rounded-lg text-2xs font-medium flex items-center justify-between transition-all duration-200 ${
                  filters[filterName] ? "bg-[#FFE100] text-black shadow-lg" : `${isDarkMode ? "bg-gray-900 text-gray-300 hover:bg-gray-800" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`
                }`}
              >
                <div className="flex items-center">
                  <span className="mr-2 text-base">
                    {filterName === "removeEmptyRows" && "🧹"}
                    {filterName === "removeEmptyColumns" && "🪄"}
                    {filterName === "removeRepeatingColumns" && "🔄"}
                    {filterName === "removeDuplicateColumns" && "👥"}
                    {filterName === "prioritizeDataDensity" && "📈"}
                    {filterName === "includeImages" && "🖼️"}
                    {filterName === "showDebugColumns" && "🐞"}
                  </span>
                  <span>
                    {filterName === "removeEmptyRows" && "Remove Empty Rows"}
                    {filterName === "removeEmptyColumns" && "Remove Empty Columns"}
                    {filterName === "removeRepeatingColumns" && "Hide Repeating Data"}
                    {filterName === "removeDuplicateColumns" && "Remove Duplicate Columns"}
                    {filterName === "prioritizeDataDensity" && "Prioritize Data-Rich Columns"}
                    {filterName === "includeImages" && "Show Images"}
                    {filterName === "showDebugColumns" && "Show Debug Columns"}
                  </span>
                </div>
              </button>
            ))}

          {/* Mostly Empty Columns Filter */}
          <div
            className={`rounded-lg border transition-all duration-200 ${
              filters.hideMostlyEmptyColumns?.enabled ? "bg-[#FFE100] border-[#FFE100] shadow-lg" : `${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-gray-100 border-gray-300"}`
            }`}
          >
            <button
              onClick={toggleMostlyEmptyColumnsFilter}
              className={`w-full px-3 py-2 text-2xs font-medium flex items-center justify-between transition-all duration-200 ${
                filters.hideMostlyEmptyColumns?.enabled ? "text-black" : `${isDarkMode ? "text-gray-300 hover:bg-gray-800" : "text-gray-700 hover:bg-gray-200"}`
              }`}
            >
              <div className="flex items-center">
                <span className="mr-2 text-base">🎯</span>
                <span>Hide Mostly Empty Columns</span>
              </div>
            </button>

            {filters.hideMostlyEmptyColumns?.enabled && (
              <div className="px-3 pb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-2xs ${filters.hideMostlyEmptyColumns?.enabled ? "text-black" : "text-gray-400"}`}>Hide if &gt; {filters.hideMostlyEmptyColumns?.threshold || 50}% empty</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={filters.hideMostlyEmptyColumns?.threshold || 50}
                    onChange={(e) => updateMostlyEmptyColumnsThreshold(parseInt(e.target.value) || 50)}
                    className="w-12 px-1 py-0.5 text-2xs rounded border bg-white text-black border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#FFAA00]"
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={filters.hideMostlyEmptyColumns?.threshold || 50}
                  onChange={(e) => updateMostlyEmptyColumnsThreshold(parseInt(e.target.value))}
                  className="w-full h-1 bg-gray-300 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #FFAA00 0%, #FFAA00 ${filters.hideMostlyEmptyColumns?.threshold || 50}%, #e5e7eb ${
                      filters.hideMostlyEmptyColumns?.threshold || 50
                    }%, #e5e7eb 100%)`,
                  }}
                />
                <div className="flex justify-between text-2xs text-black mt-1">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add a section for filter descriptions */}
        <div className={`p-4 border-t mt-4 ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}>
          <h4 className={`text-2xs font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>About Filters</h4>
          <p className={`text-2xs mb-1 ${isDarkMode ? "text-gray-500" : "text-gray-600"}`}>Filters help you clean and organize your data for better analysis.</p>
          <p className={`text-2xs ${isDarkMode ? "text-gray-500" : "text-gray-600"}`}>Toggle options above to apply different transformations to your data table.</p>
        </div>
      </div>
    </div>
  );
};

export default FiltersPanel;
